import { DIGISAKA_API_ORIGIN } from '../config/digisakaApiOrigin';
import {
  digisakaAuthRequestInit,
  digisakaMeteredAuthHeaders,
  gptInferenceJsonHeaders,
} from '../config/digisakaGptInference';

export const MAX_FARM_GROUP_MEMBERS = 20;

export type FarmGroupMember = {
  farm_id: string;
  farm_name?: string | null;
  province?: string | null;
  municipality?: string | null;
};

export type FarmGroupSummary = {
  id: string;
  name: string;
  description?: string | null;
  member_count: number;
  created_at?: string | null;
  updated_at?: string | null;
};

export type FarmGroupDetail = FarmGroupSummary & {
  members: FarmGroupMember[];
};

async function readError(res: Response): Promise<string> {
  try {
    const text = await res.text();
    if (!text) return `HTTP ${res.status}`;
    try {
      const json = JSON.parse(text);
      if (typeof json?.detail === 'string') return json.detail;
      if (Array.isArray(json?.detail)) {
        return json.detail.map((d: { msg?: string }) => d?.msg || String(d)).join('; ');
      }
    } catch {
      /* use raw text */
    }
    return text;
  } catch {
    return `HTTP ${res.status}`;
  }
}

export async function listFarmAnalysisGroups(): Promise<FarmGroupSummary[]> {
  const res = await fetch(
    `${DIGISAKA_API_ORIGIN}/api/farm-groups/`,
    digisakaAuthRequestInit({
      headers: { ...digisakaMeteredAuthHeaders() },
    })
  );
  if (!res.ok) throw new Error(await readError(res));
  const data = await res.json();
  return data.items ?? [];
}

export async function getFarmAnalysisGroup(groupId: string): Promise<FarmGroupDetail> {
  const res = await fetch(
    `${DIGISAKA_API_ORIGIN}/api/farm-groups/${encodeURIComponent(groupId)}`,
    digisakaAuthRequestInit({
      headers: { ...digisakaMeteredAuthHeaders() },
    })
  );
  if (!res.ok) throw new Error(await readError(res));
  return res.json();
}

export async function createFarmAnalysisGroup(input: {
  name: string;
  description?: string;
  members: FarmGroupMember[];
}): Promise<FarmGroupDetail> {
  const res = await fetch(
    `${DIGISAKA_API_ORIGIN}/api/farm-groups/`,
    digisakaAuthRequestInit({
      method: 'POST',
      headers: { ...gptInferenceJsonHeaders() },
      body: JSON.stringify(input),
    })
  );
  if (!res.ok) throw new Error(await readError(res));
  return res.json();
}

export async function deleteFarmAnalysisGroup(groupId: string): Promise<void> {
  const res = await fetch(
    `${DIGISAKA_API_ORIGIN}/api/farm-groups/${encodeURIComponent(groupId)}`,
    digisakaAuthRequestInit({
      method: 'DELETE',
      headers: { ...digisakaMeteredAuthHeaders() },
    })
  );
  if (!res.ok) throw new Error(await readError(res));
}
